import { Button, ButtonGroup, EditNationalId, EditPassport, Form, Input, LabelValue, Modal, Select, Toast } from "Components";
import { useCallback, useEffect, useState } from "react";
import { FaTrashAlt } from "react-icons/fa";
import { FiMail } from "react-icons/fi";
import { get, patch } from "Utils/axios";
import { passengerTypeFromDob, validatePhoneNumber } from "Utils/utils";
import { formatDobInput, parseDobInput } from "Utils/dob";
import { COUNTRIES } from "Utils/constants";
import moment from "moment";

const GENDER_OPTIONS = [
	{ label: "Male", value: "MALE" },
	{ label: "Female", value: "FEMALE" },
];

const parseGuestDob = (value) => {
	const parsed = moment(value, "DD/MM/YYYY", true);
	if (!parsed.isValid()) {
		return { error: "Please enter date of birth in DD/MM/YYYY format", value: null };
	}
	if (parsed.isAfter(moment().endOf("day"))) {
		return { error: "Date of birth cannot be in the future", value: null };
	}
	return { error: null, value: parsed.valueOf() };
};

const getTravellerEndpoint = (travellerId, isGuest) => (isGuest ? `guests/${travellerId}` : `/users/${travellerId}`);

const getCountryOptionFromPhoneCode = (value) => COUNTRIES.find((country) => country.value === value || country.phoneCode === value);

const formatPhoneDisplay = (phone, phoneCode) => {
	if (!phone) {
		return "";
	}
	const country = getCountryOptionFromPhoneCode(phoneCode);
	return country?.phoneCode ? `${country.phoneCode} ${phone}` : phone;
};

const getGenderLabel = (value) => GENDER_OPTIONS.find((option) => option.value === value)?.label || value;

const TravellerEditorModal = ({ buttonLabel, title, contentClass = "w-[95vw] max-w-[28rem]", children }) => {
	const [open, setOpen] = useState(false);

	return (
		<>
			<Button onClick={() => setOpen(true)} variant="outlined" className="btn-xs">
				{buttonLabel}
			</Button>
			<Modal open={open} onClose={() => setOpen(false)} contentClass={contentClass}>
				<div className="p-4 sm:p-6 md:p-8">
					<h1 className="text-xl sm:text-2xl font-bold pr-10">{title}</h1>
					{children({ close: () => setOpen(false) })}
				</div>
			</Modal>
		</>
	);
};

const TravellerDobEditor = ({ travellerId, isGuest, dob, onUpdated }) => {
	const [loading, setLoading] = useState(false);
	const [input, setInput] = useState(dob ? moment(dob).format("DD/MM/YYYY") : "");

	useEffect(() => {
		setInput(dob ? moment(dob).format("DD/MM/YYYY") : "");
	}, [dob]);

	const initialInput = dob ? moment(dob).format("DD/MM/YYYY") : "";
	const hasChanges = input !== initialInput;
	const parseDob = isGuest ? parseGuestDob : parseDobInput;

	const saveDob = (close) => {
		if (!input) {
			return Toast.error("Please enter date of birth");
		}
		const parsed = parseDob(input);
		if (parsed.error) {
			return Toast.error(parsed.error);
		}

		setLoading(true);
		patch(
			getTravellerEndpoint(travellerId, isGuest),
			{ dob: moment(parsed.value).format("YYYY-MM-DD") },
			(r, e) => {
				setLoading(false);
				if (r) {
					onUpdated?.({ dob: moment(parsed.value).format("YYYY-MM-DD") });
					Toast.success("Date of birth updated");
					close();
				} else if (e) {
					Toast.handleError(e);
				}
			}
		);
	};

	return (
		<TravellerEditorModal buttonLabel={dob ? moment(dob).format("DD/MM/YYYY") : "Add Date of Birth"} title="Date of Birth">
			{({ close }) => (
				<Form onSubmit={() => saveDob(close)} className="mt-4 space-y-4">
					<Input
						value={input}
						onChange={(value) => {
							if (!value) {
								setInput("");
								return;
							}
							const previous = input || "";
							const typingForward = value.length >= previous.length && value.startsWith(previous);
							setInput(typingForward ? formatDobInput(value) : value);
						}}
						onBlur={() => {
							if (!input) return;
							setInput(formatDobInput(input));
						}}
						placeholder="DD/MM/YYYY"
						maxLength={10}
						allowSpecialChars
						label="Date of Birth"
						inputClass="tracking-wide"
					/>
					{hasChanges && (
						<Button loading={loading} type="submit" className="w-full">
							Save
						</Button>
					)}
				</Form>
			)}
		</TravellerEditorModal>
	);
};

const TravellerPhoneEditor = ({ travellerId, isGuest, phone, phoneCode, onUpdated }) => {
	const [loading, setLoading] = useState(false);
	const [input, setInput] = useState(phone || "");
	const [selectedPhoneCode, setSelectedPhoneCode] = useState(getCountryOptionFromPhoneCode(phoneCode) || {});

	useEffect(() => {
		setInput(phone || "");
		setSelectedPhoneCode(getCountryOptionFromPhoneCode(phoneCode) || {});
	}, [phone, phoneCode]);

	const initialInput = phone || "";
	const initialPhoneRegion = typeof phoneCode === "object" ? phoneCode?.value : phoneCode;
	const currentPhoneRegion = selectedPhoneCode?.value || "";
	const hasChanges = input !== initialInput || currentPhoneRegion !== (initialPhoneRegion || "");

	const savePhone = async (close) => {
		if (!input) {
			return Toast.error("Please enter phone number");
		}

		const effectivePhoneRegion = currentPhoneRegion || initialPhoneRegion || "";

		if (isGuest && !effectivePhoneRegion) {
			return Toast.error("Please select phone code");
		}

		// Preserve the old user-flow behavior: only validate when a region exists.
		if (effectivePhoneRegion && !(await validatePhoneNumber(input, effectivePhoneRegion))) {
			return Toast.error("Please enter valid phone number");
		}

		const payload = {
			phone: input,
			...(effectivePhoneRegion ? { phoneCode: effectivePhoneRegion } : {}),
		};

		setLoading(true);
		patch(
			getTravellerEndpoint(travellerId, isGuest),
			payload,
			(r, e) => {
				setLoading(false);
				if (r) {
					onUpdated?.(payload);
					Toast.success("Phone number updated");
					close();
				} else if (e) {
					Toast.handleError(e);
				}
			}
		);
	};

	return (
		<TravellerEditorModal buttonLabel={formatPhoneDisplay(phone, phoneCode) || "Add Phone"} title="Phone Number">
			{({ close }) => (
				<Form onSubmit={() => savePhone(close)} className="mt-4 space-y-4">
					<div>
						<label>Phone Number</label>
						<div className="flex gap-2 mt-2">
							<Select className="max-w-min" placeholder="Code" selected={selectedPhoneCode} onChange={setSelectedPhoneCode} options={COUNTRIES} />
							<Input className="flex-1" type="number" value={input} onChange={setInput} placeholder="Phone" />
						</div>
					</div>
					{hasChanges && (
						<Button loading={loading} type="submit" className="w-full">
							Save
						</Button>
					)}
				</Form>
			)}
		</TravellerEditorModal>
	);
};

const TravellerGenderEditor = ({ travellerId, isGuest, gender, onUpdated }) => {
	const [loading, setLoading] = useState(false);
	const [selectedGender, setSelectedGender] = useState(gender || "");

	useEffect(() => {
		setSelectedGender(gender || "");
	}, [gender]);

	const hasChanges = selectedGender !== (gender || "");

	const saveGender = (close) => {
		if (!selectedGender) {
			return Toast.error("Please select gender");
		}

		setLoading(true);
		patch(
			getTravellerEndpoint(travellerId, isGuest),
			{ gender: selectedGender },
			(r, e) => {
				setLoading(false);
				if (r) {
					onUpdated?.({ gender: selectedGender });
					Toast.success("Gender updated");
					close();
				} else if (e) {
					Toast.handleError(e);
				}
			}
		);
	};

	return (
		<TravellerEditorModal buttonLabel={getGenderLabel(gender) || "Add Gender"} title="Gender">
			{({ close }) => (
				<Form onSubmit={() => saveGender(close)} className="mt-4 space-y-4">
					<ButtonGroup
						selected={selectedGender ? { label: getGenderLabel(selectedGender), value: selectedGender } : undefined}
						onClick={(option) => setSelectedGender(option.value)}
						options={GENDER_OPTIONS}
					/>
					{hasChanges && (
						<Button loading={loading} type="submit" className="w-full">
							Save
						</Button>
					)}
				</Form>
			)}
		</TravellerEditorModal>
	);
};

export const TravellerCard = ({ mandatoryField, id, isGuest, removeTraveller, airlineCode, updateTraveller, resetErrors }) => {
	const [TravellerDetails, setTravellerDetails] = useState({});

	const fetchUser = useCallback((travellerId) => {
		get(`users/${travellerId}`, {}, (r, e) => {
			if (r) {
				setTravellerDetails(r);
				updateTraveller(r);
			}
		});
	}, [updateTraveller]);

	const fetchGuest = useCallback((travellerId) => {
		get(`guests/${travellerId}`, {}, (r, e) => {
			if (r) {
				setTravellerDetails({ ...r, isGuest: true });
				updateTraveller({ ...r, isGuest: true });
			}
		});
	}, [updateTraveller]);

	const fetchDetails = useCallback(() => {
		if (isGuest) fetchGuest(id);
		else fetchUser(id);
	}, [fetchGuest, fetchUser, id, isGuest]);

	useEffect(() => {
		fetchDetails();
	}, [fetchDetails]);

	const handleUpdated = (changes = {}) => {
		if (Object.keys(changes).length > 0) {
			setTravellerDetails((prev) => {
				const next = {
					...prev,
					...changes,
					...(isGuest ? { isGuest: true } : {}),
				};
				updateTraveller(next);
				return next;
			});
		}
		resetErrors();
	};
	const passengerType = passengerTypeFromDob(TravellerDetails.dob, airlineCode);

	return (
		<div className="my-4 border rounded-md fade-in-up overflow-visible">
			{/* Header: identity + contact + action — wraps on small screens so Remove is never cut off */}
			<div className="p-3 md:p-4 bg-light rounded-t-md flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
				<div className="min-w-0 flex-1">
					<h5 className="font-semibold text-dark truncate">
						{TravellerDetails.fName} {TravellerDetails.lName} {isGuest ? "(Guest)" : ""}
						{passengerType === "ADULT" && " - Adult"}
						{passengerType === "CHILD" && " - Child"}
						{passengerType === "INFANT" && " - Infant"}
					</h5>
				</div>
				<div className="flex flex-wrap items-center gap-2 min-w-0">
					<span className="flex items-center gap-2 text-label min-w-0 truncate" title={TravellerDetails.email}>
						<FiMail className="shrink-0" aria-hidden />
						<span className="truncate">{TravellerDetails.email}</span>
					</span>
					<Button onClick={() => removeTraveller(id, isGuest)} variant="danger" className="btn-xs shrink-0">
						<FaTrashAlt className="mr-2" aria-hidden />
						Remove
					</Button>
				</div>
			</div>
			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-3 md:p-4">
				<LabelValue label="Phone">
					<TravellerPhoneEditor travellerId={id} isGuest={isGuest} phone={TravellerDetails.phone} phoneCode={TravellerDetails.phoneCode} onUpdated={handleUpdated} />
				</LabelValue>
				<LabelValue label="Gender">
					<TravellerGenderEditor travellerId={id} isGuest={isGuest} gender={TravellerDetails.gender} onUpdated={handleUpdated} />
				</LabelValue>
				<LabelValue label="Date of Birth">
					<TravellerDobEditor travellerId={id} isGuest={isGuest} dob={TravellerDetails.dob} onUpdated={handleUpdated} />
				</LabelValue>
				{mandatoryField.passportValidation && (
					<LabelValue label="Passport (Required)">
						<EditPassport
							reload={() => {
								fetchDetails();
								resetErrors();
							}}
							identification={TravellerDetails.identification}
							userId={isGuest ? undefined : id}
							guestId={isGuest ? id : undefined}
							photo={TravellerDetails.photo}
						/>
					</LabelValue>
				)}
				{mandatoryField.nationalityValidation && (
					<LabelValue label="Government ID (Required)">
						<EditNationalId
							reload={() => {
								fetchDetails();
								resetErrors();
							}}
							identification={TravellerDetails.identification}
							userId={isGuest ? undefined : id}
							guestId={isGuest ? id : undefined}
						/>
					</LabelValue>
				)}
			</div>
		</div>
	);
};
